import { MaturityOrder, MuniOrderType, PriorityTypeEnum, RetailTypeEnum } from '../deal-series.types';
import {
  breachesOrderConditions,
  formatFlexMaturityForDisplay,
  formatOrderQuantityForCell,
  getOrderAmountCap,
  hasValidOrderQuantity,
  normalizeNumericValueForDisplay,
  OptionConfig,
  orderAmountFormatter,
} from '../deal-series.util';

export const returnEmptyStringIfZeroOrFalsey = (value: string | number) => {
  if (value == null || value === '' || String(value) === '0' || Number(value) === 0) return '';
  return value;
};

export const orderTypeOptions: OptionConfig[] = [
  { value: MuniOrderType.INDIVIDUAL_RETAIL, displayValue: 'Indiv Retail' },
  { value: MuniOrderType.PROFESSIONAL_RETAIL, displayValue: 'Prof Retail' },
  { value: MuniOrderType.OTHER, displayValue: 'Other' },
];

/** Dropdown submits LOCAL | STATE | NATIONAL (matches BOND_ORDER.RETAIL_TYPE / API 3 retailType). */
export const retailTypeOptions: OptionConfig[] = [
  { value: RetailTypeEnum.LOCAL, displayValue: 'LOCAL' },
  { value: RetailTypeEnum.STATE, displayValue: 'STATE' },
  { value: RetailTypeEnum.NATIONAL, displayValue: 'NATIONAL' },
];

/** API-supported priority types only (NONE is unset / empty dropdown). */
export const priorityTypeOptions: OptionConfig[] = [
  { value: PriorityTypeEnum.MEMBER, displayValue: 'Mem' },
  { value: PriorityTypeEnum.GROUP_NET, displayValue: 'GN' },
  { value: PriorityTypeEnum.NET_DESIGNATED, displayValue: 'ND' },
];

export const getPriorityTypeDropdownValue = (priorityType?: string): string => {
  if (
    priorityType === PriorityTypeEnum.MEMBER ||
    priorityType === PriorityTypeEnum.GROUP_NET ||
    priorityType === PriorityTypeEnum.NET_DESIGNATED
  ) {
    return priorityType;
  }
  return '';
};

export const getRetailTypeDropdownValue = (retailType?: string): string => {
  if (
    retailType === RetailTypeEnum.LOCAL ||
    retailType === RetailTypeEnum.STATE ||
    retailType === RetailTypeEnum.NATIONAL
  ) {
    return retailType;
  }
  return '';
};

export const getOrderTypeDropdownValue = (orderType?: string): string => {
  if (
    orderType === MuniOrderType.INDIVIDUAL_RETAIL ||
    orderType === MuniOrderType.PROFESSIONAL_RETAIL ||
    orderType === MuniOrderType.OTHER
  ) {
    return orderType;
  }

  return MuniOrderType.OTHER;
};

export const hasConditionsWarning = (order: MaturityOrder): boolean => {
  if (!order) {
    return false;
  }

  const quantity = order?.QUANTITY;
  // No warning for empty/null/zero quantities
  if (!quantity || parseFloat(String(quantity)) === 0) {
    return false;
  }

  const quantityStr = String(quantity);

  if (!hasValidOrderQuantity(quantityStr, getOrderAmountCap(order))) {
    return false;
  }

  return breachesOrderConditions(
    quantityStr,
    order.MINIMUM_ORDER_AMOUNT,
    order.MAXIMUM_ORDER_AMOUNT
  );
};

export const getQuantityWarningMessage = (order: MaturityOrder): string => {
  if (!hasConditionsWarning(order)) {
    return '';
  }
  const quantity = order.QUANTITY ? parseFloat(String(order.QUANTITY)) : 0;
  const minNum = order.MINIMUM_ORDER_AMOUNT ? parseFloat(String(order.MINIMUM_ORDER_AMOUNT)) : 0;
  const maxNum = order.MAXIMUM_ORDER_AMOUNT ? parseFloat(String(order.MAXIMUM_ORDER_AMOUNT)) : 0;
  if (minNum > 0 && quantity < minNum) {
    return 'Order Amount below Minimum Condition';
  }
  if (maxNum > 0 && quantity > maxNum) {
    return 'Order Amount exceeds Maximum Condition';
  }
  return '';
};

export type NumericOrderAmountField = 'QUANTITY' | 'MINIMUM_ORDER_AMOUNT' | 'MAXIMUM_ORDER_AMOUNT';

export const formatOrderAmount = (order: MaturityOrder, field: NumericOrderAmountField): string => {
  if (field === 'QUANTITY') {
    return formatOrderQuantityForCell(order);
  }
  const raw = order?.[field];
  if (raw === null || raw === undefined || String(raw).trim() === '') {
    return '';
  }
  const value = parseFloat(String(raw));
  if (isNaN(value)) {
    return '';
  }
  if (value === 0) {
    return '';
  }
  return orderAmountFormatter(value);
};

export const getFlexMaturityDisplayValue = (
  flexMaturityId: string | undefined,
  flexMaturityOptions: MaturityOrder[]
): string => {
  return formatFlexMaturityForDisplay(flexMaturityId, flexMaturityOptions);
};

export const normalizeNumericFieldForDisplay = normalizeNumericValueForDisplay;
